import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useFormContext, Controller } from 'react-hook-form';
import clsx from 'classnames';

import Grid from '@material-ui/core/Grid';
import SearchIcon from '@material-ui/icons/Search';
import TextField from 'components/CncUI/TextField';
import Radio from 'components/CncUI/Radio';
import Button from 'components/CncUI/Button';
import Logger from 'utilities/logger';
import Autocomplete from '@material-ui/lab/Autocomplete';

import isEmail from 'utilities/validateEmail';
import isOktaId from 'utilities/validateOktaId';
import useQuery from 'hooks/useQuery';
import Alert from 'components/CncUI/Alert';
import Typography from 'components/CncUI/Typography';
import CheckIcon from '@material-ui/icons/Check';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import ErrorIcon from '@material-ui/icons/ErrorOutline';

import './AutoComplete.css';

const useStyles = makeStyles(({ spacing, palette }) => ({
  userContainer: {
    marginBottom: spacing(2),
    paddingRight: spacing(2),
    '& .cncui-alert-icon': {
      margin: '20px 0px',
    },
    '& .cncui-alert-message': {
      margin: '0px',
      width: '100%',
    },
  },
  radioLabel: {
    marginTop: -4,
  },
  groupSelectContainer: {
    paddingRight: spacing(2),
  },
  button: {
    marginTop: spacing(2),
    marginLeft: spacing(1),
    width: 145,
  },
  clearButton: {
    width: 'auto',
    marginBottom: spacing(3),
  },
  oid: {
    color: 'gray',
    marginLeft: spacing(2),
  },
  successIcon: {
    color: palette.cncUI.alert.green.primary,
  },
  errorIcon: {
    color: palette.cncUI.alert.red.primary,
  },
  option: {
    padding: '9px 16px',
  },
  selected: {
    width: '100%',
    backgroundColor: `${palette.cncUI.default['+9']} !important`,
    color: `${palette.cncUI.default['+0']} !important`,
  },
  autoComplete: {
    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      padding: '6px 0 7px',
    },
  },
}));

const NotificationRecipient = props => {
  const classes = useStyles();
  const logger = Logger('Notification_SearchUser');
  const {
    loading,
    onSearch,
    onFound,
    onClear: parentClear,
    user,
    groups,
    state,
    modify,
  } = props;
  const { register, control, errors, setValue, watch } = useFormContext();
  const [inputValue, setInputValue] = useState('');

  const userSearchValue = watch('search');
  const referenceType = watch('referenceType');
  const groupSelectValue = watch('groupSelect');
  const selectedGroup =
    (groups || []).find(g => g.id === groupSelectValue?.id) || {};

  const findUser = useQuery('getActiveUser', {
    scopes: ['notification:create', 'openid'],
    fetchPolicy: 'network-only',
    request: ({ loginId, email }) => ({ loginId, email }),
    response: res => {
      modify({ submitDisabled: false });
      return res.data.getActiveUser;
    },
  });

  const searchUser = async () => {
    onSearch();
    const search = isEmail(userSearchValue)
      ? { email: userSearchValue }
      : { loginId: userSearchValue };
    try {
      const result = await findUser(search);
      onFound(result);
      modify({ searchError: '' });
    } catch (error) {
      logger.error(error);
      parentClear();
      modify({ searchError: error.message || error });
    }
  };

  const inputValidationRules = {
    required: 'Missing email or Okta ID',
    validate: value =>
      isEmail(value) || isOktaId(value) || 'Please enter a valid email or ID',
  };

  const onKeyDown = async e => {
    const { charCode, keyCode } = e || {};
    // Enter = key #13
    if (charCode === 13 || keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
      if (!loading) searchUser();
    }
  };

  const clearSearch = () => {
    setValue('search', '');
    setValue('groupSelect', '');
    modify({ submitDisabled: true, searchError: '' });
    parentClear();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} container>
        <Typography variant="h5">Notification Recipient</Typography>
      </Grid>
      <Grid item xs={12} lg={2} container>
        <Controller
          render={props => (
            <Radio.Group
              id="NotificationRecipient-radioGroup"
              name={props.name}
              value={props.value}
              onChange={e => {
                props.onChange(e.currentTarget.value);
                clearSearch();
              }}
              row
              label="Type"
              labelProps={{ className: classes.radioLabel }}
            >
              <Radio value="loginId" id="NotificationRecipient-loginId">
                Single user
              </Radio>
              <Radio value="groupId" id="NotificationRecipient-groupId">
                Okta group
              </Radio>
            </Radio.Group>
          )}
          name="referenceType"
          rules={{ required: 'Missing selection' }}
          control={control}
        />
      </Grid>
      <Grid item xs={12} lg={10} container>
        {referenceType === 'loginId' && (
          <>
            <Grid item xs={12} sm={8}>
              <TextField
                light
                fullWidth
                id="SearchField"
                name="search"
                label="Single Recipient"
                placeholder="Search for Okta user (Email or Okta ID)"
                onKeyDown={onKeyDown}
                startIcon={<SearchIcon />}
                inputRef={register(inputValidationRules)}
                error={Boolean(errors.search)}
                helperText={errors.search && errors.search.message}
                style={{ marginBottom: 8 }}
              />
            </Grid>
            <Grid item xs={3} sm={3}>
              <Button
                id="SearchButton"
                loading={loading}
                disabled={Boolean(errors.search)}
                className={classes.button}
                onClick={searchUser}
                secondary
              >
                Find User
              </Button>
            </Grid>
          </>
        )}
        {referenceType === 'groupId' && (
          <Grid item xs={11} className={classes.groupSelectContainer}>
            <Controller
              render={props => (
                <Autocomplete
                  id={`NotificationRecipient-${props.name}`}
                  placeholder="Select an Okta group"
                  value={props.value}
                  inputValue={inputValue}
                  onChange={(e, newValue) => {
                    modify({ submitDisabled: false });
                    props.onChange(newValue);
                  }}
                  onInputChange={(e, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  renderOption={option => (
                    <div
                      className={clsx(
                        classes.option,
                        selectedGroup.id === option.id && classes.selected,
                      )}
                      key={option.id}
                    >
                      <div>
                        <b>{option.name}</b>
                      </div>
                      <div>
                        {option?.description || 'No description available'}
                      </div>
                    </div>
                  )}
                  options={groups}
                  getOptionLabel={option => option?.name || ''}
                  className={classes.autoComplete}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Group Recipient"
                      light
                      fullWidth
                      placeholder="Select an Okta group"
                      startIcon={<SearchIcon />}
                      error={Boolean(errors?.search)}
                      helperText={errors?.search?.message}
                      style={{ marginBottom: 8 }}
                    />
                  )}
                />
              )}
              name="groupSelect"
              rules={{ required: 'Missing selection' }}
              control={control}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={11} container className={classes.userContainer}>
          {((user && user.loginId) ||
            (referenceType === 'groupId' && groupSelectValue)) && (
            <Alert
              success
              fullWidth
              icon={'span'}
              noBorder
              className={classes.userDisplay}
            >
              <Grid item xs={12} container direction="row" alignItems="center">
                <Grid
                  item
                  xs={2}
                  sm={1}
                  container
                  direction="column"
                  alignItems="center"
                >
                  <CheckIcon className={classes.successIcon} />
                </Grid>
                {user && user.loginId && (
                  <>
                    <Grid item xs={10} sm={8}>
                      <Typography id="userRecord">
                        <b>
                          {user.firstName} {user.lastName}
                        </b>{' '}
                        <span className={classes.oid}>{user.loginId}</span>
                        <br />
                        {user.email}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={3}
                      container
                      direction="column"
                      alignItems="center"
                    >
                      <Button
                        id="ClearButton"
                        loading={loading}
                        tertiary
                        onClick={clearSearch}
                        endIcon={<RemoveIcon />}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </>
                )}
                {referenceType === 'groupId' && selectedGroup && (
                  <Grid item xs={10}>
                    <Typography id="groupRecord">
                      <b>{selectedGroup.name}</b> <br />
                      {selectedGroup.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Alert>
          )}
          {state.searchError && (
            <Alert
              error
              fullWidth
              icon={'span'}
              noBorder
              className={classes.errorDisplay}
            >
              <Grid container direction="row" alignItems="center">
                <Grid
                  item
                  xs={2}
                  sm={1}
                  container
                  direction="column"
                  alignItems="center"
                >
                  <ErrorIcon className={classes.errorIcon} />
                </Grid>
                <Grid item xs={10} sm={11}>
                  <Typography>
                    <b>{state.searchError}</b>
                    <br />
                    {state.searchError === 'User not found'
                      ? 'A user with that email or Okta ID couldn’t be found. Check for errors and try again.'
                      : state.searchError === 'User not registered in MyCnC'
                      ? 'User exists in Okta but not as an active MyCnC user.'
                      : 'An error occurred while trying to fetch user data'}
                  </Typography>
                </Grid>
              </Grid>
            </Alert>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default NotificationRecipient;
